import {useMutation, useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/ProductsService';
import {useState} from 'react';

export const useProducts = () => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = {column: 'created', direction: 'desc' as const};

    const productsQuery = useInfiniteQuery({
        queryKey: ['products', keyword, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['products']});
        }
    });

    const fetchRecordsAction = () => {
        if (productsQuery.hasNextPage) {
            productsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids: string[]) => {
        if (ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = productsQuery.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: productsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: productsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
